import { useTranslator } from "@eo-locale/react";

import range from "lodash/range";

import { string } from "yup";

import dayjs from "dayjs";

import {
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  Select,
  useBreakpointValue,
  Calendar,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "@/types";
import { setAnswer } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

import useEmbeds from "@/hooks/useEmbeds";

interface DateQuestionProps {
  question: Question;
}

export const validate = async (
  answer: string,
  question: Question
): Promise<string[]> => {
  let schema = string().nullable();
  if (question.required) {
    schema = schema.required();
  }
  try {
    await schema.validate(answer);
    return [];
  } catch (err) {
    return err.errors;
  }
};

const DateQuestion = ({ question }: DateQuestionProps): JSX.Element => {
  const { translate, language } = useTranslator();
  const dispatch = useAppDispatch();
  const { isInsideIframe } = useEmbeds();
  const size = useBreakpointValue({ base: "lg", lg: "md" });
  const { region } = useAppSelector((state) => state.site.site);
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );

  const error = errors[question.id] || [];
  const answer = answers[question.id] || "";

  const handleYearChange = (e: { value: number }): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: e?.value ? e.value : null
      })
    );
  };

  const handleChange = (date: Date) => {
    dispatch(
      setAnswer({
        questionId: question.id,
        answer: date ? dayjs(date).format("YYYY-MM-DD") : ""
      })
    );
  };

  const renderYearOnly = () => {
    const currentYear = new Date().getFullYear();
    const currentValue = new Date(answer).getFullYear();

    const year = currentValue
      ? { label: currentValue, value: currentValue }
      : null;
    const options = range(currentYear - 100, currentYear + 100);

    return (
      <Select
        isClearable
        value={year}
        size={size}
        menuPlacement="auto"
        isSearchable={false}
        onChange={handleYearChange}
        classNamePrefix="ehq-dropdown"
        className="ehq-dropdownContainer"
        placeholder={translate("questions.date.selectYear")}
        innerProps={{ "data-testid": "dateQuestion-yearSelect" }}
        options={options.map((option) => ({
          label: option,
          value: new Date(option, 1, 1).toISOString() as unknown as number
          //Date(?, ?, ?) 3 numbers specify year, month, and day
        }))}
      />
    );
  };

  return (
    <FormControl
      my={4}
      id={`${question.id}`}
      isInvalid={error.length > 0}
      isRequired={question.required}
      isDisabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <FormLabel>
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </FormLabel>
      {question.yearOnlyDate ? (
        renderYearOnly()
      ) : (
        <Calendar
          onChange={handleChange}
          disabled={status === "saving"}
          size={size}
          selected={answer ? new Date(answer) : null}
          locale={language === "en" ? "en-AU" : language}
          placeholderText={translate("questions.date.selectDate")}
          dateFormat={region === "united_states" ? "MM-dd-yyyy" : "dd-MM-yyyy"}
          isInsideIframe={isInsideIframe}
        />
      )}
      {error.length > 0 && (
        <FormErrorMessage data-testid="dateQuestion-errorMessage">
          <FormErrorIcon />
          {translate(`errors.${error}`)}
        </FormErrorMessage>
      )}
      <FormHelperText>{question.notes}</FormHelperText>
    </FormControl>
  );
};

export default DateQuestion;
